import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode, type RefObject } from 'react';
import { navigateToSearch } from './lib/use-hash-route';
import { AskHost } from './lib/ask';
import { labels } from './lib/labels';
import { useSession } from './lib/session';
import type { Theme } from './lib/theme';

// One 24px line icon per destination, plus the two theme icons. Decorative: the link's or button's
// name is its visible-or-hidden label.
const ICONS = {
  menu: 'M4 6h16M4 12h16M4 18h16',
  processing: 'M3 12h4l3-8 4 16 3-8h4',
  documents: 'M6 3h8l4 4v14H6zM14 3v4h4',
  notProcessed: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 8v5M12 16h.01',
  testExtraction: 'M9 3h6M10 3v6l-5 10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1l-5-10V3',
  extractionRules: 'M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01',
  settings: 'M4 8h10M18 8h2M4 16h2M10 16h10M16 5v6M8 13v6',
  // A shield with a person in it: user management, a level above Settings.
  userAdmin:
    'M12 3l8 3v6c0 4.5-3.2 7.7-8 9-4.8-1.3-8-4.5-8-9V6zM12 9a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM9 17c.6-1.5 1.7-2 3-2s2.4.5 3 2',
  external: 'M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5',
  moon: 'M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z',
  sun: 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4',
  account: 'M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM4 21c0-4 4-6 8-6s8 2 8 6',
} as const;

const NAV = [
  { href: 'processing', icon: 'processing', label: labels.nav.processing },
  { href: 'documents', icon: 'documents', label: labels.nav.documents },
  { href: 'not-processed', icon: 'notProcessed', label: labels.nav.notProcessed },
  { href: 'test-extraction', icon: 'testExtraction', label: labels.nav.testExtraction },
  { href: 'extraction-rules', icon: 'extractionRules', label: labels.nav.extractionRules },
  { href: 'settings', icon: 'settings', label: labels.nav.settings },
] as const;

function Icon({ name, className = 'app-icon' }: { name: keyof typeof ICONS; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d={ICONS[name]} />
    </svg>
  );
}

/**
 * Closes the open popup on Escape, on a press outside `ref`, and when focus moves out of `ref`.
 * `viaEscape` tells the caller to give focus back to the trigger; the other two leave focus where
 * the user put it.
 */
function useDismiss(open: boolean, ref: RefObject<HTMLElement>, close: (viaEscape: boolean) => void) {
  useEffect(() => {
    const popup = ref.current;
    if (!open || !popup) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close(true);
    };
    const onPress = (event: Event) => {
      if (!popup.contains(event.target as Node)) close(false);
    };
    // A null `relatedTarget` is a click on the page (the press handler has it) or a window blur,
    // which leaves the popup open.
    const onFocusOut = (event: FocusEvent) => {
      if (event.relatedTarget && !popup.contains(event.relatedTarget as Node)) close(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPress);
    popup.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPress);
      popup.removeEventListener('focusout', onFocusOut);
    };
  }, [open, ref, close]);
}

/**
 * Home + a persistent header search field + the navigation rail.
 * **OPS-02's addition**:
 * the field is live, not a stub — typing and submitting navigates to `#documents?q=<query>`
 * (`use-hash-route.ts`'s `navigateToSearch`), the one non-nav way onto the search screen. The
 * field is controlled by `searchQuery` (the *current* route's own `q`, if any) so a pasted
 * `#documents?q=...` link, or navigating away and back, shows the right value rather than a blank
 * box next to a populated results screen.
 *
 * The shell is a side rail (N3), a header with an account menu, and a Ft4 dense colophon
 * (`design.md` § What screens MUST share). Search is the widest thing in the header because the interrupt lookup is the one
 * time-pressured task and it starts by typing. `route` drives `aria-current` on whichever
 * destination the operator is standing on — the accent's first job on every screen.
 */
export function AppShell({
  children,
  searchQuery = '',
  routeKey = '',
  route = '',
  onSignOut,
  theme = 'light',
  onToggleTheme = () => {},
}: {
  children: ReactNode;
  searchQuery?: string;
  routeKey?: string;
  route?: string;
  onSignOut: () => void;
  theme?: Theme;
  onToggleTheme?: () => void;
}) {
  const { username, role, superAdmin, userAdminUrl } = useSession();
  const [value, setValue] = useState(searchQuery);
  const mainRef = useRef<HTMLElement>(null);
  // QA-92: the rail and the profile menu are the console's only surfaces above the page besides the
  // dialog. Neither state is saved: a reload starts both closed.
  // One state for both: opening one closes the other, and only the open one is dismissed.
  const [popup, setPopup] = useState<'rail' | 'menu' | null>(null);
  const railOpen = popup === 'rail';
  const menuOpen = popup === 'menu';
  const railRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const accountRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<HTMLButtonElement>(null);
  const signOutRef = useRef<HTMLButtonElement>(null);

  const closeRail = useCallback((viaEscape: boolean) => {
    setPopup(null);
    if (viaEscape) toggleRef.current?.focus();
  }, []);
  const closeMenu = useCallback((viaEscape: boolean) => {
    setPopup(null);
    if (viaEscape) avatarRef.current?.focus();
  }, []);
  useDismiss(railOpen, railRef, closeRail);
  useDismiss(menuOpen, accountRef, closeMenu);

  // A route change closes both. It does not move focus: `main` takes it below.
  useEffect(() => {
    setPopup(null);
  }, [routeKey]);

  useEffect(() => {
    if (menuOpen) signOutRef.current?.focus();
  }, [menuOpen]);

  useEffect(() => {
    setValue(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    // React commits a descendant's passive effect before this one, so if the route's own content
    // already focused something more specific (e.g. a failure-group heading on a `#not-processed`
    // context change), `document.activeElement` is already inside `main` here and this fallback
    // must not steal it back. Only claim focus when nothing more specific has.
    if (main.contains(document.activeElement)) return;
    main.focus();
  }, [routeKey]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    navigateToSearch(value);
  }

  // `aria-current="page"` is the accessible name for the underline, so the two can never
  // disagree: `undefined` removes the attribute rather than rendering `aria-current="false"`.
  // Collapsed, the label stays in the DOM as the link's name and is hidden from sight only.
  const labelClass = railOpen ? undefined : 'visually-hidden';
  const current = (href: string) => (route === href ? 'page' : undefined);

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header__inner">
          <a className="app-brand" href="#processing" aria-current={current('processing')}>
            {/* The app name is the document's `h1` and the screen title below it is an `h2`, so
              every route has a complete heading tree: a screen whose most important heading is an
              `h2` with no `h1` above it reads as a fragment to a screen reader. */}
            <h1 className="app-brand__name">{labels.appName}</h1>
          </a>
          <form className="app-search" role="search" onSubmit={handleSubmit}>
            <input
              type="search"
              placeholder={labels.searchPlaceholder}
              aria-label={labels.searchPlaceholder}
              value={value}
              onChange={(event) => setValue(event.target.value)}
            />
          </form>
          {/* The rail runs down the start edge. The toggle sits outside the `nav` landmark, so the
              landmark holds only the destinations: six routes, and for a super admin the link to the
              user admin console after them (SSO-22). Collapsed, a link shows its icon and keeps
              its label as a visually hidden name; expanded, the rail overlays the page. */}
          <div className="app-rail" data-open={railOpen || undefined} ref={railRef}>
            <button
              type="button"
              className="app-rail__toggle"
              ref={toggleRef}
              aria-expanded={railOpen}
              aria-controls="app-nav"
              onClick={() => setPopup((open) => (open === 'rail' ? null : 'rail'))}
            >
              <Icon name="menu" />
              <span className={labelClass}>{labels.nav.toggle}</span>
            </button>
            <nav id="app-nav" className="app-nav" onClick={() => setPopup(null)}>
              {NAV.map(({ href, icon, label }) => (
                <a key={href} href={`#${href}`} aria-current={current(href)}>
                  <Icon name={icon} />
                  <span className={labelClass}>{label}</span>
                </a>
              ))}
              {/* SSO-22: the Keycloak user admin console, for a super admin only. It is a link to
                  another app, so it opens in a new tab, is never the current page, and says so in its
                  name. Keycloak decides every right there. */}
              {superAdmin && userAdminUrl && (
                <a
                  href={userAdminUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={labels.nav.userAdminNewTab}
                  title={labels.nav.userAdminNewTab}
                >
                  <Icon name="userAdmin" />
                  <span className={labelClass}>{labels.nav.userAdmin}</span>
                  {railOpen && <Icon name="external" className="app-icon app-nav__external" />}
                </a>
              )}
            </nav>
          </div>
          {/* Sign out acts on the session, not on a page, so it sits outside the navigation
              landmark, in its own group at the end of the header, behind the avatar. */}
          <div className="app-account" ref={accountRef}>
            {/* The label names the action, so there is no `aria-pressed`. */}
            <button
              type="button"
              className="app-account__theme"
              aria-label={theme === 'dark' ? labels.nav.themeToLight : labels.nav.themeToDark}
              onClick={() => {
                onToggleTheme();
                setPopup(null);
              }}
            >
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
            </button>
            <button
              type="button"
              className="app-account__avatar"
              ref={avatarRef}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-controls="app-account-menu"
              aria-label={labels.nav.account}
              onClick={() => setPopup((open) => (open === 'menu' ? null : 'menu'))}
            >
              <Icon name="account" />
            </button>
            <div id="app-account-menu" className="app-account__menu" role="menu" hidden={!menuOpen}>
              {/* Who is signed in: the name from Keycloak and the role that sets what the screens let them do. */}
              <p className="app-account__who" role="none">
                <span className="app-account__name">{username}</span>
                <span className="app-account__role">
                  {superAdmin ? labels.nav.superAdminName : labels.nav.roleName[role]}
                </span>
              </p>
              <button type="button" role="menuitem" ref={signOutRef} onClick={onSignOut}>
                {labels.login.signOut}
              </button>
            </div>
          </div>
        </div>
      </header>
      <main className="app-main" ref={mainRef} tabIndex={-1}>
        {children}
      </main>
      <footer className="app-colophon">
        <span>{labels.appName}</span>
        {/* The one thing an operator with a staging tab and a production tab open needs to
          know before they click a control that mails a human. Read off the URL rather than
          a build-time constant, because the built bundle is served by the backend it talks
          to: the host *is* the environment. */}
        <span>
          {labels.colophon.api} <span className="app-colophon__env">{window.location.host}</span>
        </span>
      </footer>
      {/* One dialog for the whole app (`lib/ask.tsx`), mounted outside `main` so the guarded
          write that opens it does not move it in the DOM. */}
      <AskHost />
    </div>
  );
}
